import { useState, useEffect, useRef } from "react";

const API_BASE = "https://iw8hnsfb2e.execute-api.us-east-1.amazonaws.com/Prod";

const themes = {
  light: {
    bg: "#FFFFFF", sidebar: "#F7F9FC", text: "#0A0A0A", textSub: "#555555",
    accent: "#2563EB", accentLight: "#EFF6FF", border: "#E5E7EB",
    card: "#FFFFFF", input: "#F9FAFB", bubble: "#F0F4FF", bubbleAi: "#F9FAFB",
    danger: "#DC2626", success: "#059669", hover: "#F3F4F6"
  },
  dark: {
    bg: "#0A0A0A", sidebar: "#111111", text: "#F5F5F5", textSub: "#999999",
    accent: "#FFFFFF", accentLight: "#1A1A1A", border: "#222222",
    card: "#141414", input: "#1A1A1A", bubble: "#1A1A1A", bubbleAi: "#1C1C1C",
    danger: "#EF4444", success: "#10B981", hover: "#1A1A1A"
  }
};

async function apiCall(path, method, body, token) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const opts = { method, headers };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${API_BASE}${path}`, opts);
  return res.json();
}

async function uploadFileToS3(file, agentType, token) {
  const presign = await apiCall("/upload", "POST", {
    fileName: file.name,
    fileType: file.type || "application/octet-stream",
    agentType
  }, token);
  if (presign.error) throw new Error(presign.error);
  const putRes = await fetch(presign.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file
  });
  if (!putRes.ok) throw new Error(`S3 upload failed: ${putRes.status}`);
  return { s3Key: presign.s3Key, name: file.name, type: file.type || "application/octet-stream" };
}

function Icon({ name, size = 18, color }) {
  const s = { width: size, height: size, display: "inline-block", verticalAlign: "middle" };
  const c = color || "currentColor";
  const icons = {
    send: <svg style={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/></svg>,
    stop: <svg style={s} viewBox="0 0 24 24" fill={c}><rect x="4" y="4" width="16" height="16" rx="2"/></svg>,
    attach: <svg style={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>,
    sun: <svg style={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>,
    moon: <svg style={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>,
    users: <svg style={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87m-4-12a4 4 0 010 7.75"/></svg>,
    tooth: <svg style={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2"><path d="M12 2C8 2 5 5 5 8c0 4 2 6 3 10 .5 2 1 4 2 4s1.5-2 2-4c1-4 3-6 3-10 0-3-3-6-7-6z"/></svg>,
    bill: <svg style={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>,
    book: <svg style={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>,
    x: <svg style={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>,
    logout: <svg style={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4m7 14l5-5-5-5m5 5H9"/></svg>,
    plus: <svg style={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2"><path d="M12 5v14m-7-7h14"/></svg>,
    trash: <svg style={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2"><path d="M3 6h18m-2 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>,
    loader: <svg style={{...s, animation: "spin 1s linear infinite"}} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2"><path d="M12 2v4m0 12v4m-7.07-3.07l2.83-2.83m8.48-8.48l2.83-2.83M2 12h4m12 0h4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83"/></svg>,
    menu: <svg style={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2"><path d="M3 12h18M3 6h18M3 18h18"/></svg>,
  };
  return icons[name] || null;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

function FileUploadBox({ label, files: boxFiles, setFilesState, theme }) {
  const t = themes[theme];
  const [inputKey, setInputKey] = useState(Date.now());

  function handleChange(e) {
    const selected = Array.from(e.target.files || []);
    if (selected.length > 0) setFilesState(prev => [...prev, ...selected]);
    setInputKey(Date.now());
  }

  function removeFile(i, e) {
    e.preventDefault();
    e.stopPropagation();
    setFilesState(prev => prev.filter((_, idx) => idx !== i));
    setInputKey(Date.now());
  }

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4, fontWeight: 500 }}>{label}</div>
      <label style={{ border: `1px dashed ${t.border}`, borderRadius: 8, padding: 10, textAlign: "center", cursor: "pointer", background: t.input, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 4 }}>
        <input key={inputKey} type="file" multiple accept="*/*" onChange={handleChange} style={{ display: "none" }} />
        {boxFiles.length === 0
          ? <span style={{ color: t.textSub, fontSize: 12 }}><Icon name="attach" size={14} /> Upload</span>
          : boxFiles.map((f, i) => (
              <span key={i} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, background: t.accentLight, padding: "2px 8px", borderRadius: 4, color: t.text }}>
                {f.name.length > 15 ? f.name.slice(0, 15) + "..." : f.name}
                <button onClick={(e) => removeFile(i, e)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: t.textSub, display: "flex", alignItems: "center" }}>
                  <Icon name="x" size={10} />
                </button>
              </span>
            ))
        }
      </label>
    </div>
  );
}

function LoginPage({ onLogin, theme, toggleTheme }) {
  const t = themes[theme];
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function handleLogin(e) {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const data = await apiCall("/auth/login", "POST", { username: email, password });
      if (data.error) { setErr(data.error); setLoading(false); return; }
      onLogin(data);
    } catch (ex) { setErr("Connection failed. Check your network."); }
    setLoading(false);
  }

  return (
    <div style={{ minHeight: "100vh", background: t.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", transition: "background 0.3s", padding: "20px 16px", boxSizing: "border-box" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <button onClick={toggleTheme} style={{ position: "fixed", top: 16, right: 16, background: t.card, border: `1px solid ${t.border}`, borderRadius: 8, padding: "8px 12px", cursor: "pointer", color: t.text }}>
        <Icon name={theme === "dark" ? "sun" : "moon"} color={t.text} />
      </button>
      <div style={{ width: "100%", maxWidth: 400, padding: "32px 24px", background: t.card, borderRadius: 16, border: `1px solid ${t.border}`, boxShadow: "0 4px 24px rgba(0,0,0,0.08)", boxSizing: "border-box" }}>
        <h1 style={{ color: t.text, fontSize: 22, fontWeight: 600, margin: 0 }}>MolarIQ</h1>
        <p style={{ color: t.textSub, fontSize: 14, margin: "8px 0 32px" }}>AI Dashboard — Sign in to continue</p>
        {err && <div style={{ background: "#FEE2E2", color: "#991B1B", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{err}</div>}
        <form onSubmit={handleLogin}>
          <label style={{ color: t.textSub, fontSize: 13, display: "block", marginBottom: 6 }}>Email</label>
          <input value={email} onChange={e => setEmail(e.target.value)} type="email" required style={{ width: "100%", padding: "12px 14px", background: t.input, border: `1px solid ${t.border}`, borderRadius: 8, color: t.text, fontSize: 16, marginBottom: 16, boxSizing: "border-box", outline: "none" }} />
          <label style={{ color: t.textSub, fontSize: 13, display: "block", marginBottom: 6 }}>Password</label>
          <div style={{ position: "relative", marginBottom: 24 }}>
            <input value={password} onChange={e => setPassword(e.target.value)} type={showPw ? "text" : "password"} required style={{ width: "100%", padding: "12px 14px", background: t.input, border: `1px solid ${t.border}`, borderRadius: 8, color: t.text, fontSize: 16, boxSizing: "border-box", outline: "none" }} />
            <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: t.textSub, cursor: "pointer", fontSize: 12 }}>{showPw ? "Hide" : "Show"}</button>
          </div>
          <button type="submit" disabled={loading} style={{ width: "100%", padding: "14px", background: theme === "dark" ? "#FFFFFF" : "#0A0A0A", color: theme === "dark" ? "#0A0A0A" : "#FFFFFF", border: "none", borderRadius: 8, fontSize: 16, fontWeight: 500, cursor: loading ? "wait" : "pointer", opacity: loading ? 0.7 : 1 }}>
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ChatWindow({ messages, theme, isLoading }) {
  const t = themes[theme];
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, isLoading]);
  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
      {messages.length === 0 && <p style={{ color: t.textSub, textAlign: "center", marginTop: 40, fontSize: 14 }}>Start a conversation...</p>}
      {messages.map((m, i) => (
        <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 12 }}>
          <div style={{ maxWidth: "88%", padding: "10px 14px", borderRadius: 12, background: m.role === "user" ? (theme === "dark" ? "#222" : "#0A0A0A") : t.bubbleAi, color: m.role === "user" ? "#FFF" : t.text, fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {m.content.split(/(\bhttps?:\/\/\S+)/g).map((part, i) =>
              /^https?:\/\//.test(part)
                ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: "#2563EB", textDecoration: "underline", wordBreak: "break-all" }}>{part}</a>
                : part
            )}
            {m.files && m.files.length > 0 && <div style={{ marginTop: 6, fontSize: 12, color: m.role === "user" ? "#CCC" : t.textSub }}>{m.files.map(f => f.name).join(", ")}</div>}
          </div>
        </div>
      ))}
      {isLoading && <div style={{ display: "flex", gap: 4, padding: 10 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: t.textSub, animation: "pulse 1s infinite" }} /><span style={{ width: 8, height: 8, borderRadius: "50%", background: t.textSub, animation: "pulse 1s infinite 0.2s" }} /><span style={{ width: 8, height: 8, borderRadius: "50%", background: t.textSub, animation: "pulse 1s infinite 0.4s" }} /></div>}
      <div ref={endRef} />
    </div>
  );
}

function ChatInput({ onSend, theme, isLoading, onStop, showAttach = true, canSendEmpty = false }) {
  const t = themes[theme];
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [inputKey, setInputKey] = useState(Date.now());
  const fileRef = useRef(null);

  function handleSend() {
    if (!text.trim() && files.length === 0 && !canSendEmpty) return;
    onSend(text, files);
    setText("");
    setFiles([]);
    setInputKey(Date.now());
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  function handleFiles(e) {
    const newFiles = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...newFiles]);
    setInputKey(Date.now());
  }

  function removeFile(idx) {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setInputKey(Date.now());
  }

  return (
    <div style={{ borderTop: `1px solid ${t.border}`, padding: "10px 12px" }}>
      {files.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          {files.map((f, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", background: t.accentLight, borderRadius: 6, fontSize: 12, color: t.text }}>
              {f.name.length > 20 ? f.name.slice(0, 20) + "..." : f.name}
              <button onClick={() => removeFile(i)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: t.textSub }}><Icon name="x" size={12} /></button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {showAttach && (
          <>
            <button onClick={() => fileRef.current?.click()} style={{ background: "none", border: "none", cursor: "pointer", padding: 8, color: t.textSub, flexShrink: 0 }}><Icon name="attach" size={20} /></button>
            <input key={inputKey} ref={fileRef} type="file" multiple accept="*/*" onChange={handleFiles} style={{ display: "none" }} />
          </>
        )}
        <input value={text} onChange={e => setText(e.target.value)} onKeyDown={handleKeyDown} placeholder="Type a message..." style={{ flex: 1, padding: "10px 14px", background: t.input, border: `1px solid ${t.border}`, borderRadius: 8, color: t.text, fontSize: 16, outline: "none", minWidth: 0 }} />
        {isLoading ? (
          <button onClick={onStop} style={{ background: t.danger, border: "none", borderRadius: 8, padding: "10px 14px", cursor: "pointer", flexShrink: 0 }}><Icon name="stop" color="#FFF" /></button>
        ) : (
          <button onClick={handleSend} style={{ background: theme === "dark" ? "#FFF" : "#0A0A0A", border: "none", borderRadius: 8, padding: "10px 14px", cursor: "pointer", flexShrink: 0 }}><Icon name="send" size={16} color={theme === "dark" ? "#0A0A0A" : "#FFF"} /></button>
        )}
      </div>
    </div>
  );
}

function NarrativePage({ theme, token }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [doctorNotes, setDoctorNotes] = useState("");
  const [xrayFiles, setXrayFiles] = useState([]);
  const [perioFiles, setPerioFiles] = useState([]);
  const [dnFiles, setDnFiles] = useState([]);
  const [dnInputKey, setDnInputKey] = useState(Date.now());
  const t = themes[theme];
  const controllerRef = useRef(null);
  const hasContent = doctorNotes.trim().length > 0 || xrayFiles.length > 0 || perioFiles.length > 0 || dnFiles.length > 0;

  async function handleSend(chatText, chatFiles) {
    const allFiles = [...xrayFiles, ...perioFiles, ...dnFiles, ...chatFiles];
    const displayText = chatText || doctorNotes || "Analyze uploaded files";
    const userMsg = { role: "user", content: displayText, files: allFiles.map(f => ({ name: f.name })) };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    controllerRef.current = new AbortController();
    try {
      const uploadedFiles = [];
      for (const file of allFiles) {
        const uploaded = await uploadFileToS3(file, "narrative", token);
        uploadedFiles.push(uploaded);
      }
      const data = await apiCall("/narrative", "POST", {
        message: chatText || "Please analyze and generate a narrative.",
        doctorNotes: doctorNotes,
        files: uploadedFiles,
        history: messages.filter(m => m.role === "user" || m.role === "assistant").map(m => ({ role: m.role, content: m.content })),
        sessionId: `narrative-${Date.now()}`
      }, token);
      if (data.error) setMessages(prev => [...prev, { role: "assistant", content: `Error: ${data.error}` }]);
      else setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
    } catch (err) {
      if (err.name !== "AbortError") setMessages(prev => [...prev, { role: "assistant", content: `Error: ${err.message}` }]);
    }
    setLoading(false);
    setDoctorNotes("");
    setXrayFiles([]); setPerioFiles([]); setDnFiles([]);
    setDnInputKey(Date.now());
  }

  function handleStop() { controllerRef.current?.abort(); setLoading(false); }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <ChatWindow messages={messages} theme={theme} isLoading={loading} />
      <ChatInput onSend={handleSend} theme={theme} isLoading={loading} onStop={handleStop} canSendEmpty={hasContent} />
    </div>
  );
}

function BillingPage({ theme, token, endpoint }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const controllerRef = useRef(null);

  async function handleSend(text, files) {
    const userMsg = { role: "user", content: text, files: files.map(f => ({ name: f.name })) };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    controllerRef.current = new AbortController();
    try {
      const uploadedFiles = [];
      for (const file of files) {
        const uploaded = await uploadFileToS3(file, "billing", token);
        uploadedFiles.push(uploaded);
      }
      const data = await apiCall(endpoint || "/billing", "POST", {
        message: text, files: uploadedFiles,
        history: messages.filter(m => m.role === "user" || m.role === "assistant").map(m => ({ role: m.role, content: m.content })),
        sessionId: `billing-${Date.now()}`
      }, token);
      if (data.error) setMessages(prev => [...prev, { role: "assistant", content: `Error: ${data.error}` }]);
      else setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
    } catch (err) {
      if (err.name !== "AbortError") setMessages(prev => [...prev, { role: "assistant", content: `Error: ${err.message}` }]);
    }
    setLoading(false);
  }

  function handleStop() { controllerRef.current?.abort(); setLoading(false); }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <ChatWindow messages={messages} theme={theme} isLoading={loading} />
      <ChatInput onSend={handleSend} theme={theme} isLoading={loading} onStop={handleStop} />
    </div>
  );
}

function AdminPage({ theme, token }) {
  const t = themes[theme];
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", password: "" });
  const [actionMsg, setActionMsg] = useState("");
  const [adminTab, setAdminTab] = useState("users");
  const [prompts, setPrompts] = useState({ narrative: "", billing: "", training: "" });
  const [selectedAgent, setSelectedAgent] = useState("narrative");
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptMsg, setPromptMsg] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  async function loadUsers() {
    setLoading(true);
    const data = await apiCall("/auth/users", "GET", null, token);
    if (data.users) setUsers(data.users);
    setLoading(false);
  }

  useEffect(() => { loadUsers(); }, []);

  async function createUser() {
    if (!newUser.username || !newUser.password) return;
    const data = await apiCall("/auth/users/create", "POST", newUser, token);
    if (data.error) setActionMsg(`Error: ${data.error}`);
    else { setActionMsg("User created"); setShowCreate(false); setNewUser({ username: "", password: "" }); loadUsers(); }
    setTimeout(() => setActionMsg(""), 3000);
  }

  async function toggleUser(username, enabled) {
    const path = enabled ? "/auth/users/disable" : "/auth/users/enable";
    await apiCall(path, "POST", { username }, token);
    loadUsers();
  }

  async function deleteUser(username) {
    if (!window.confirm(`Delete user ${username}?`)) return;
    await apiCall("/auth/users/delete", "POST", { username }, token);
    loadUsers();
  }

  async function loadPrompts() {
    try {
      const data = await apiCall("/auth/admin/prompts", "GET", null, token);
      if (data.prompts) setPrompts(data.prompts);
    } catch (e) {}
  }

  async function savePrompt() {
    setPromptSaving(true);
    setShowConfirm(false);
    try {
      const data = await apiCall("/auth/admin/prompts", "PUT", { agent: selectedAgent, prompt: prompts[selectedAgent] }, token);
      if (data.error) setPromptMsg(`Error: ${data.error}`);
      else setPromptMsg("Prompt saved successfully!");
    } catch (e) {
      setPromptMsg("Failed to save prompt.");
    }
    setPromptSaving(false);
    setTimeout(() => setPromptMsg(""), 3000);
  }

  const agentLabels = { narrative: "Narrative Writer", billing: "Billing Assistant", training: "Support-Hub" };

  return (
    <div style={{ padding: 16, overflowY: "auto", height: "100%", boxSizing: "border-box" }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        <button onClick={() => setAdminTab("users")} style={{ padding: "8px 20px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 500, background: adminTab === "users" ? (theme === "dark" ? "#FFF" : "#0A0A0A") : t.input, color: adminTab === "users" ? (theme === "dark" ? "#0A0A0A" : "#FFF") : t.text }}>Users</button>
        <button onClick={() => { setAdminTab("prompts"); loadPrompts(); }} style={{ padding: "8px 20px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 500, background: adminTab === "prompts" ? (theme === "dark" ? "#FFF" : "#0A0A0A") : t.input, color: adminTab === "prompts" ? (theme === "dark" ? "#0A0A0A" : "#FFF") : t.text }}>Prompts</button>
      </div>

      {adminTab === "prompts" && (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            {Object.keys(agentLabels).map(agent => (
              <button key={agent} onClick={() => setSelectedAgent(agent)} style={{ padding: "8px 16px", borderRadius: 6, border: `1px solid ${t.border}`, cursor: "pointer", fontSize: 13, background: selectedAgent === agent ? t.accentLight : "none", color: t.text, fontWeight: selectedAgent === agent ? 500 : 400 }}>{agentLabels[agent]}</button>
            ))}
          </div>
          {promptMsg && <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13, background: promptMsg.startsWith("Error") || promptMsg.startsWith("Failed") ? "#FEE2E2" : "#D1FAE5", color: promptMsg.startsWith("Error") || promptMsg.startsWith("Failed") ? "#991B1B" : "#065F46" }}>{promptMsg}</div>}
          <textarea
            value={prompts[selectedAgent]}
            onChange={e => setPrompts(prev => ({ ...prev, [selectedAgent]: e.target.value }))}
            rows={15}
            style={{ width: "100%", padding: "12px", background: t.input, border: `1px solid ${t.border}`, borderRadius: 8, color: t.text, fontSize: 13, resize: "vertical", boxSizing: "border-box", outline: "none", fontFamily: "monospace", lineHeight: 1.6 }}
            placeholder="Loading prompt..."
          />
          <button onClick={() => setShowConfirm(true)} disabled={promptSaving} style={{ marginTop: 12, padding: "12px 24px", background: theme === "dark" ? "#FFF" : "#0A0A0A", color: theme === "dark" ? "#0A0A0A" : "#FFF", border: "none", borderRadius: 8, cursor: promptSaving ? "wait" : "pointer", fontSize: 14, fontWeight: 500, opacity: promptSaving ? 0.7 : 1 }}>
            {promptSaving ? "Saving..." : "Save Prompt"}
          </button>
          {showConfirm && (
            <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
              <div style={{ background: t.card, borderRadius: 12, padding: 24, width: "100%", maxWidth: 360, border: `1px solid ${t.border}` }}>
                <h3 style={{ color: t.text, fontSize: 16, margin: "0 0 12px" }}>Save Prompt?</h3>
                <p style={{ color: t.textSub, fontSize: 13, margin: "0 0 20px" }}>This will update the {agentLabels[selectedAgent]} prompt immediately. Are you sure?</p>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button onClick={() => setShowConfirm(false)} style={{ padding: "10px 16px", background: "none", border: `1px solid ${t.border}`, borderRadius: 6, color: t.text, cursor: "pointer", fontSize: 14 }}>Cancel</button>
                  <button onClick={savePrompt} style={{ padding: "10px 16px", background: theme === "dark" ? "#FFF" : "#0A0A0A", color: theme === "dark" ? "#0A0A0A" : "#FFF", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14, fontWeight: 500 }}>Save</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {adminTab === "users" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h2 style={{ color: t.text, fontSize: 18, fontWeight: 600, margin: 0 }}>User Management</h2>
            <button onClick={() => setShowCreate(true)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 16px", background: theme === "dark" ? "#FFF" : "#0A0A0A", color: theme === "dark" ? "#0A0A0A" : "#FFF", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer" }}><Icon name="plus" size={14} color={theme === "dark" ? "#0A0A0A" : "#FFF"} /> Create User</button>
          </div>
          {actionMsg && <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13, background: actionMsg.startsWith("Error") ? "#FEE2E2" : "#D1FAE5", color: actionMsg.startsWith("Error") ? "#991B1B" : "#065F46" }}>{actionMsg}</div>}
          {showCreate && (
            <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
              <div style={{ background: t.card, borderRadius: 12, padding: 24, width: "100%", maxWidth: 360, border: `1px solid ${t.border}` }}>
                <h3 style={{ color: t.text, fontSize: 16, margin: "0 0 16px" }}>Create New User</h3>
                <label style={{ color: t.textSub, fontSize: 13, display: "block", marginBottom: 4 }}>Username (email)</label>
                <input value={newUser.username} onChange={e => setNewUser(p => ({ ...p, username: e.target.value }))} type="email" style={{ width: "100%", padding: "10px 12px", background: t.input, border: `1px solid ${t.border}`, borderRadius: 6, color: t.text, fontSize: 16, marginBottom: 12, boxSizing: "border-box", outline: "none" }} />
                <label style={{ color: t.textSub, fontSize: 13, display: "block", marginBottom: 4 }}>Password</label>
                <input value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} type="text" style={{ width: "100%", padding: "10px 12px", background: t.input, border: `1px solid ${t.border}`, borderRadius: 6, color: t.text, fontSize: 16, marginBottom: 20, boxSizing: "border-box", outline: "none" }} />
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button onClick={() => setShowCreate(false)} style={{ padding: "10px 16px", background: "none", border: `1px solid ${t.border}`, borderRadius: 6, color: t.text, cursor: "pointer", fontSize: 14 }}>Cancel</button>
                  <button onClick={createUser} style={{ padding: "10px 16px", background: theme === "dark" ? "#FFF" : "#0A0A0A", color: theme === "dark" ? "#0A0A0A" : "#FFF", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14, fontWeight: 500 }}>Create</button>
                </div>
              </div>
            </div>
          )}
          <div style={{ background: t.card, borderRadius: 10, border: `1px solid ${t.border}`, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                  {["Username", "Status", "Enabled", "Actions"].map(h => <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 12, color: t.textSub, fontWeight: 500, whiteSpace: "nowrap" }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan={4} style={{ padding: 20, textAlign: "center", color: t.textSub }}><Icon name="loader" /> Loading...</td></tr> :
                  users.length === 0 ? <tr><td colSpan={4} style={{ padding: 20, textAlign: "center", color: t.textSub }}>No users found</td></tr> :
                  users.map(u => (
                    <tr key={u.username} style={{ borderBottom: `1px solid ${t.border}` }}>
                      <td style={{ padding: "10px 16px", color: t.text, fontSize: 13 }}>{u.email || u.username}</td>
                      <td style={{ padding: "10px 16px" }}><span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 10, fontWeight: 500, background: u.status === "CONFIRMED" ? "#D1FAE5" : "#FEF3C7", color: u.status === "CONFIRMED" ? "#065F46" : "#92400E", whiteSpace: "nowrap" }}>{u.status}</span></td>
                      <td style={{ padding: "10px 16px" }}><span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 10, fontWeight: 500, background: u.enabled ? "#D1FAE5" : "#FEE2E2", color: u.enabled ? "#065F46" : "#991B1B" }}>{u.enabled ? "Active" : "Disabled"}</span></td>
                      <td style={{ padding: "10px 16px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => toggleUser(u.username, u.enabled)} style={{ padding: "6px 10px", fontSize: 11, borderRadius: 4, cursor: "pointer", background: "none", border: `1px solid ${t.border}`, color: t.text, whiteSpace: "nowrap" }}>{u.enabled ? "Disable" : "Enable"}</button>
                          <button onClick={() => deleteUser(u.username)} style={{ padding: "6px 10px", fontSize: 11, borderRadius: 4, cursor: "pointer", background: "none", border: `1px solid ${t.danger}`, color: t.danger }}><Icon name="trash" size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [theme, setTheme] = useState("light");
  const [auth, setAuth] = useState(null);
  const [page, setPage] = useState("narrative");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = useIsMobile();

  function toggleTheme() { setTheme(t => t === "light" ? "dark" : "light"); }
  function handleLogin(data) { setAuth(data); }
  function handleLogout() { setAuth(null); setPage("narrative"); }

  if (!auth) return <LoginPage onLogin={handleLogin} theme={theme} toggleTheme={toggleTheme} />;

  const t = themes[theme];
  const navItems = [
    { id: "narrative", label: "Narrative Writer", icon: "tooth" },
    { id: "billing", label: "Billing Assistant", icon: "bill" },
    { id: "training", label: "Support-Hub", icon: "book" },
  ];
  if (auth.role === "ADMIN" || auth.role === "admin") navItems.push({ id: "admin", label: "User Management", icon: "users" });

  const currentLabel = navItems.find(n => n.id === page)?.label;

  const SidebarContent = ({ onItemClick }) => (
    <>
      <nav style={{ flex: 1, padding: "8px" }}>
        {navItems.map(item => (
          <button key={item.id} onClick={() => { setPage(item.id); onItemClick && onItemClick(); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: page === item.id ? t.accentLight : "transparent", border: "none", borderRadius: 8, cursor: "pointer", color: page === item.id ? t.text : t.textSub, fontSize: 14, fontWeight: page === item.id ? 500 : 400, marginBottom: 2, textAlign: "left" }}>
            <Icon name={item.icon} size={18} color={page === item.id ? t.text : t.textSub} /> {item.label}
          </button>
        ))}
      </nav>
      <div style={{ padding: "12px 8px", borderTop: `1px solid ${t.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px" }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", background: t.accentLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: t.text, flexShrink: 0 }}>{(auth.username || "U")[0].toUpperCase()}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: t.text, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{auth.username}</div>
            <div style={{ fontSize: 10, color: t.textSub }}>{auth.role}</div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", background: t.bg, transition: "background 0.3s", overflow: "hidden" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes pulse{0%,100%{opacity:.3}50%{opacity:1}} * {scrollbar-width:thin;scrollbar-color:${t.border} transparent} input:focus,textarea:focus{border-color:${t.textSub}!important}`}</style>

      {/* Desktop Sidebar */}
      {!isMobile && (
        <div style={{ width: 220, background: t.sidebar, borderRight: `1px solid ${t.border}`, display: "flex", flexDirection: "column", transition: "background 0.3s", flexShrink: 0 }}>
          <div style={{ padding: "20px 16px", borderBottom: `1px solid ${t.border}` }}>
            <h2 style={{ color: t.text, fontSize: 15, fontWeight: 600, margin: 0 }}>MolarIQ</h2>
          </div>
          <SidebarContent />
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {isMobile && sidebarOpen && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 300 }}>
          <div onClick={() => setSidebarOpen(false)} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)" }} />
          <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: 270, background: t.sidebar, display: "flex", flexDirection: "column", zIndex: 301 }}>
            <div style={{ padding: "16px", borderBottom: `1px solid ${t.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ color: t.text, fontSize: 15, fontWeight: 600, margin: 0 }}>MolarIQ</h2>
              <button onClick={() => setSidebarOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: t.textSub, padding: 4 }}><Icon name="x" size={22} /></button>
            </div>
            <SidebarContent onItemClick={() => setSidebarOpen(false)} />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {isMobile && (
              <button onClick={() => setSidebarOpen(true)} style={{ background: "none", border: "none", cursor: "pointer", color: t.text, padding: 4, display: "flex", alignItems: "center" }}>
                <Icon name="menu" size={22} />
              </button>
            )}
            <h1 style={{ color: t.text, fontSize: 15, fontWeight: 600, margin: 0 }}>{currentLabel}</h1>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={toggleTheme} style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 6, padding: "6px 10px", cursor: "pointer", color: t.text, display: "flex", alignItems: "center" }}><Icon name={theme === "dark" ? "sun" : "moon"} size={14} /></button>
            <button onClick={handleLogout} style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 6, padding: "6px 10px", cursor: "pointer", color: t.textSub, display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
              <Icon name="logout" size={14} />
              {!isMobile && " Sign Out"}
            </button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: "hidden" }}>
          {page === "narrative" && <NarrativePage theme={theme} token={auth.token} />}
          {page === "billing" && <BillingPage theme={theme} token={auth.token} />}
          {page === "training" && <BillingPage theme={theme} token={auth.token} endpoint="/training" />}
          {page === "admin" && <AdminPage theme={theme} token={auth.token} />}
        </div>
      </div>
    </div>
  );
}
